import crypto from 'crypto'
import { useSearchParams } from 'react-router-dom'
import CryptoJS from 'crypto-js'
import { camelCase, compact } from 'lodash'
import { parse } from 'papaparse'
import { createContext, useMemo } from 'react'
import { convertUrlFiltersToES } from './filter.utils'
import { legacyRoleRank, type LegacyRoleName } from 'app/types/roles'
import type { ImportFile } from 'app/components/ImportWizard'
import { Organization } from 'app/types/entities'
import type { Mutation } from 'app/generated/graphql'
import type { User } from 'app/types/entities'
import { Workspace } from 'app/types/entities'
import { useAuthStore } from 'app/stores/StoreContext'
import { graphql } from 'app/generated/gql'
import { gqlMutation } from 'app/lib/graphqlClient'
import { debug, logError, trace } from 'lib/logging'

const SWITCH_WORKSPACE_MUTATION = graphql(`
  mutation SwitchWorkspace($targetWorkspaceId: String) {
    switchWorkspace(targetWorkspaceId: $targetWorkspaceId) {
      accessToken
    }
  }
`)

const SWITCH_WORKSPACE_ORG_MUTATION = graphql(`
  mutation SwitchWorkspaceOrg($targetWorkspaceId: String, $targetOrgId: String) {
    switchWorkspace(targetWorkspaceId: $targetWorkspaceId, targetOrgId: $targetOrgId) {
      accessToken
    }
  }
`)
import { Jsonify } from 'lib/toJSON'

const DEFAULT_REDIRECT = `/`

export const PAGE_LIMIT = 1000
export const UUID_REGEX = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i

export type Falsy = null | undefined | false | 0 | -0 | ``

/**
 * This should be used any time the redirect path is user-provided
 * (Like the query string on our login/signup pages). This avoids
 * open-redirect vulnerabilities.
 * @param {string} to The redirect destination
 * @param {string} defaultRedirect The redirect to use if the to is unsafe.
 */
export function safeRedirect(
  to: FormDataEntryValue | string | null | undefined,
  defaultRedirect: string = DEFAULT_REDIRECT,
): string {
  if (!to || typeof to !== `string`) {
    return defaultRedirect
  }

  if (!to.startsWith(`/`) || to.startsWith(`//`)) {
    return defaultRedirect
  }

  return to
}

/**
 * This base hook is used in other hooks to quickly search for specific data.
 * Now reads from the auth store instead of Remix route matches.
 */
export function useMatchesData(_id: string): Record<string, unknown> | undefined {
  const auth = useAuthStore()
  if (!auth.user) return undefined
  return {
    user: auth.user,
    userPermissions: { permissions: auth.permissions, isOrgRole: auth.isOrgRole },
  } as any
}

export function useOptionalUser(_routeId?: string): Jsonify<User> | undefined {
  const auth = useAuthStore()
  return auth.user as any
}

export function useWorkspace(): Jsonify<Workspace> | undefined {
  const auth = useAuthStore()
  return auth.workspace as Jsonify<Workspace> | undefined
}
export function useOrganization(): Jsonify<Organization> | undefined {
  const auth = useAuthStore()
  return auth.user ? ({ id: auth.user.orgId } as any) : undefined
}

export function useUser(_routeId?: string): Jsonify<User> | undefined {
  const auth = useAuthStore()
  return auth.user as Jsonify<User> | undefined
}

export function hasRole(user: { role?: number }, roleName?: LegacyRoleName): boolean {
  if (!roleName) return true
  if (!user.role) {
    return false
  }
  return user.role >= legacyRoleRank[roleName]
}

export function validateEmail(email: unknown): email is string {
  return typeof email === `string` && email.length > 3 && email.includes(`@`)
}

export async function setWorkspace(workspaceId: string | null, redirectTo?: string, tries = 0) {
  trace(`switch`, `setWorkspace`, workspaceId, redirectTo)
  const result = await gqlMutation(SWITCH_WORKSPACE_MUTATION, {
    targetWorkspaceId: workspaceId,
  })
  if (result.error) {
    // eslint-disable-next-line no-console
    console.error(`error setting workspace`, result.error)
    if (tries > 3) throw new Error(result.error.message)
    return setWorkspace(workspaceId, redirectTo, tries + 1)
  }
  // When no explicit redirectTo, navigate to `/` so the index route's fallbackRedirect
  // routes to the correct default page based on workspace products (e.g. CRM vs AI Agent only)
  let url = new URL(document.location.origin + (redirectTo || `/`))
  url.searchParams.set(`wId`, workspaceId || `null`)
  debug(`switch`, `navigating to`, url.toString())
  location.assign(url.toString())
}

export async function setOrg(orgId: string | null, redirectTo?: string, tries = 0) {
  trace(`switch`, `setOrg`, orgId, redirectTo)
  const result = await gqlMutation(SWITCH_WORKSPACE_ORG_MUTATION, {
    targetWorkspaceId: null,
    targetOrgId: orgId,
  })
  if (result.error) {
    logError(`setOrg`, `failed to switch org`, result.error.message)
    if (tries > 3) throw new Error(result.error.message)
    return setOrg(orgId, redirectTo, tries + 1)
  }
  let url = new URL(document.location.origin + (redirectTo || `/`))
  url.searchParams.set(`wId`, `null`)
  location.assign(url.toString())
}

export function useHandleSearchParams(): (
  param: { [key: string]: string | undefined },
  options?: { replace?: boolean },
) => boolean {
  const setSearchParams = useSearchParams()[1]
  return (params: { [key: string]: string | undefined }, options?: { replace?: boolean }) => {
    let searchParams = new URL(window.location.toString()).searchParams
    const allSearchParams = Object.fromEntries(searchParams.entries())
    let hasChanges = false
    for (const [key, value] of Object.entries(params)) {
      // An undefined value means "remove this param". Always strip it from both
      // maps so it can never reach setSearchParams and serialize as the literal
      // string "undefined" (which happens when the key isn't already in the URL).
      // Checked before the `page` skip below so a caller can still drop `page`
      // (e.g. resetting to page 0 on a filter change) without it leaking through.
      if (value === undefined) {
        if (key in allSearchParams) {
          delete allSearchParams[key]
          hasChanges = true
        }
        delete params[key]
        continue
      }
      if (key === `page`) continue
      // eslint-disable-next-line eqeqeq -- we want undefined and null to be equal
      if (searchParams.get(key) != value) {
        hasChanges = true
      }
    }

    if (hasChanges) {
      setSearchParams(
        {
          ...allSearchParams,
          ...params,
        } as any,
        { replace: options?.replace ?? false },
      )
    }
    return hasChanges
  }
}

export function getTableSearchParams(request: Request): {
  page: number
  terms: string[]
  sort?: string
  limit?: 0 | 1 | 100 | 1000
} {
  const { searchParams } = new URL(request.url)
  let page = Number(searchParams.get(`page`) || 0)
  let query = searchParams.get(`query`) ?? ``
  let join = searchParams.get(`join`)
  const urlFilters = convertUrlFiltersToES(searchParams.get(`filters`), join)
  const searchAndFilters = compact([query, ...urlFilters])
  let sort = searchParams.get(`sort`) ?? undefined
  if (!sort) {
    sort = undefined
  }
  const limit = (Number(searchParams.get(`limit`)) as 0 | 1 | 100 | 1000) || undefined
  return { page, terms: searchAndFilters, sort, limit }
}

export function getTextFirstLine(text: string | undefined): string[] | undefined {
  if (!text) return undefined
  return text
    .slice(0, text.indexOf(`\n`) + 1)
    .replace(/\s|\*/g, ``)
    .split(`,`)
    ?.map((text) => camelCase(text))
}

export function csvToArray(csv: string | null): ImportFile[] | string {
  if (!csv) return `No file found`
  let rows: ImportFile[] = []
  parse(csv, {
    header: true,
    transformHeader: (header) => camelCase(header.replace(/\s|\*/g, ``)),
    complete: (results) => (rows = results.data as any),
  })
  return rows
}

export function md5(str: string) {
  if (typeof window !== `undefined`) {
    return CryptoJS.MD5(str).toString()
  }
  return crypto.createHash(`md5`).update(str).digest(`hex`)
}

export function sha256(str: string) {
  debug(`sha`, `sha256`, str)
  if (!str) return undefined
  if (typeof window !== `undefined`) {
    return CryptoJS.SHA256(str).toString()
  }
  return crypto.createHash(`sha256`).update(str).digest(`hex`)
}

export function replaceCamelCase(role: string): string {
  return role.replace(/([A-Z])/g, ` $1`)
}

export function capitalizeCamelCase(str: string): string | undefined {
  const words = str.match(/[A-Za-z][a-z]*/g)
  const capitalizedWords = words?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  return capitalizedWords?.join(` `)
}

export function sanitizeValue(value: string | string[] | undefined): string | string[] {
  return Array.isArray(value)
    ? (value?.map((val) => val!.replace(/[+-=&&||><!(){}[]^"~*?:\\\/]/g, ``)) ?? ``)
    : (value?.replace(/[+-=&&||><!(){}[]^"~*?:\\\/]/g, ``) ?? ``)
}

export function divide(left: number | undefined, right: number | undefined) {
  if (!left || !right) return 0
  return Math.round((left / right) * 100) / 100
}

export function isLocationIntegrated(location?: { pmsIntegrated?: boolean }): boolean {
  if (!location) return false
  return location.pmsIntegrated === true
}

export const CollapsedContext = createContext(false)
